import {
  attendanceApi,
  type AttendanceEnterBody,
  type AttendanceListParams,
  type AttendanceQuitBody,
  type AttendanceSheetParams,
  type DateAttendances,
} from '@mathflat/domain/Attendance'

import { mpEventInfo } from '@/constant'
import type { SchoolGrade, Student } from '@/dto'
import { Attendance, AttendanceByStudentId } from '@/dto'
import type { Day } from '@/dto/common/Calendar/Day'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

type PeriodParams = Omit<AttendanceListParams, 'deduplicated'>

class AttendanceController implements Controller {
  adapter = {
    dateAttendanceList: makeAdapter({
      from: (data: DateAttendances[]) => {
        return new AttendanceByStudentId(data)
      },
    }),
  }

  async fetchAttendances(params: PeriodParams) {
    const data = await attendanceApi.getAttendances(
      { ...params, deduplicated: false },
      { mp_event_name: mpEventInfo.출결조회 },
    )

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchGradeAttendances(
    schoolType: SchoolGrade['schoolType'],
    grade: SchoolGrade['grade'],
    params: PeriodParams,
  ) {
    const data = await attendanceApi.getGradeAttendances(
      schoolType,
      grade,
      { ...params, deduplicated: false },
      { mp_event_name: mpEventInfo.출결조회 },
    )

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchLessonClassAttendances(lessonClassId: number, params: PeriodParams) {
    const data = await attendanceApi.getLessonClassAttendances(
      lessonClassId,
      { ...params, deduplicated: false },
      { mp_event_name: mpEventInfo.출결조회 },
    )

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchStudentAttendances(studentId: Student['id'], params: PeriodParams) {
    const data = await attendanceApi.getStudentAttendances(
      studentId,
      { ...params, deduplicated: false },
      { mp_event_name: mpEventInfo.출결조회 },
    )

    return this.adapter.dateAttendanceList.from(data)
  }

  async fetchSpecificDayAttendances(
    studentIdList: Student['id'][],
    day: Day,
    deduplicated = false,
  ) {
    return attendanceApi.getAttendances(
      { startDate: day.toString, endDate: day.toString, deduplicated },
      { mp_event_name: mpEventInfo.출결조회 },
    )
  }

  async enterAttendance(body: AttendanceEnterBody) {
    const data = await attendanceApi.enterAttendance(body, {
      mp_event_name: mpEventInfo.학생입실등록,
    })

    return data.map((attendance) => new Attendance(attendance))
  }

  async quitAttendance(body: AttendanceQuitBody) {
    const data = await attendanceApi.quitAttendance(body, {
      mp_event_name: mpEventInfo.학생퇴실등록,
    })

    return data.map((attendance) => new Attendance(attendance))
  }

  async deleteAttendances(ids: Attendance['id'][]) {
    await attendanceApi.deleteAttendances(ids, { mp_event_name: mpEventInfo.출결삭제 })
  }

  async getXLS(params: AttendanceSheetParams) {
    return attendanceApi.getAttendanceSheet(params, { mp_event_name: mpEventInfo.출결다운로드 })
  }
}

const attendanceController = new AttendanceController()

export default attendanceController
